import React, { useState, useEffect } from "react";
import { Column, Row, Text, Icon, Button } from "@amzn/meridian";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { COLORS } from "src/constants/colors";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import StepRenderer from "./StepRenderer";
import { setCurrentStep, setCurrentIterateStep } from "src/store/workflowSlice";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import redoTokens from "@amzn/meridian-tokens/base/icon/redo"
import { useDualStreamCapture } from "src/components/pages/inspection/hooks/useDualStreamCapture";
import { WorkflowStep, IterateStep } from "./types/inspectionWorkflow";
import { CameraStreamProps } from "./types/cameraStreamProps";

/**
 * Returns true when the active step (or active iterate sub-step) requires camera access.
 * For iterate steps, checks `currentIterateStep` (the active sub-step from Redux),
 */
function needsCamera(
  step: WorkflowStep | IterateStep | null,
  currentIterateStep: WorkflowStep | null
): boolean {
  if (currentIterateStep) {
    return 'type' in currentIterateStep && currentIterateStep.type === 'detect_object';
  }
  if (step && 'type' in step) {
    return step.type === 'detect_object';
  }
  return false;
}

const WorkflowExecutor = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    activeWorkflow,
    showWorkflowHeader,
    currentStep,
    currentIterateStep,
    inspectionId
  } = useAppSelector((state) => state.workflow);
  const { resolutionScale } = useAppSelector((state) => state.inspectionCapture.streamingConfig);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [drumCount, setDrumCount] = useState<number | null>(null);
  const { flags } = useAppSelector((state) => state.featureFlags);


  useEffect(() => {
    if (!activeWorkflow) return;
    setCurrentCategoryIndex(0);
    setCurrentStepIndex(0);
    dispatch(setCurrentStep(activeWorkflow.categories[0]?.steps[0]));
  }, [activeWorkflow?.template_id, dispatch]);

  // Shared camera streams — lifecycle controlled by step type
  const {
    lowResVideoRef, highResVideoRef,
    captureHighResFrame,
    streamsReady,
    startStreams, stopStreams,
  } = useDualStreamCapture(resolutionScale, 1.0);

  // Start/stop camera streams based on whether the active step needs camera
  useEffect(() => {
    const controller = new AbortController();

    if (needsCamera(currentStep, currentIterateStep)) {
      startStreams(controller.signal);
    } else {
      stopStreams();
    }

    return () => {
      controller.abort();
    }
  }, [currentStep, currentIterateStep, startStreams, stopStreams]);

  const cameraStreamProps: CameraStreamProps = {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    streamsReady,
  };

  if (!activeWorkflow) {
    return (
      <Column height="100vh" alignmentVertical="center" alignmentHorizontal="center">
        <Text type="h200">No active workflow</Text>
        <Button type="icon" onClick={() => navigate("/configurationDrivenInspections")}>
          <Icon tokens={redoTokens} color="primary" />
        </Button>
      </Column>
    );
  }

  const currentCategory = activeWorkflow.categories[currentCategoryIndex];

  const handleStepComplete = (data?: any) => {
    // If this was a drum count step, store the count
    if (data?.drumCount !== undefined) {
      setDrumCount(data.drumCount);
    }

    // Move to next step
    if (currentStepIndex < currentCategory.steps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      dispatch(setCurrentStep(currentCategory.steps[nextStepIndex]));
      setCurrentStepIndex(nextStepIndex);
    } else if (currentCategoryIndex < activeWorkflow.categories.length - 1) {
      // Move to next category
      const nextCategoryIndex = currentCategoryIndex + 1;
      const nextCategory = activeWorkflow.categories[nextCategoryIndex];
      dispatch(setCurrentStep(nextCategory.steps[0]));
      setCurrentCategoryIndex(nextCategoryIndex);
      setCurrentStepIndex(0);
    } else {
      dispatch(setCurrentStep(null));
      dispatch(setCurrentIterateStep(null));
      navigate(`/configurationDrivenInspections/${inspectionId}/review`);
    }
  };

  return (
    <Column
      className="workflow-executor-viewport pwa-fullscreen-viewport"
      height="100vh"
      backgroundColor={COLORS.VERY_DARK_GRAY}
      alignmentVertical="justify"
    >
      {/* Header */}
      {showWorkflowHeader && <Column
        width="100%"
        spacingInset="500 300"
        className="inspection-info-wrapper"
      >
        <Row>
          <div
            style={{ cursor: "pointer", marginLeft: "50px" }}
            onClick={() => {
              dispatch(resetInspectionCaptureState());
              dispatch(setCurrentStep(null));
              dispatch(setCurrentIterateStep(null));
              navigate(flags.opsLive ? "/home" : "/configurationDrivenInspections");
            }}
          >
            <Icon tokens={chevronLeftLargeTokens} color="inverted" />
          </div>
          <Text color="inverted" type="h200">{currentCategory.human_text}</Text>
        </Row>
      </Column>}


      {currentStep !== null && <StepRenderer
        step={currentStep}
        drumCount={drumCount}
        onStepComplete={handleStepComplete}
        cameraStreamProps={cameraStreamProps}
      />}

    </Column>
  );
};

export default WorkflowExecutor;
